import { Box, Grid, Text } from '@mantine/core';

import { useContext } from 'react';
import Form from 'components/form';
import useFormHandler from 'hooks/useFormHandler';
import { z } from 'zod';
import AuthContext from 'context/AuthContext';
import { useNotification } from 'context/NotificationContext';
import { logger, ErrorType } from '../../../utils/logger';

import classes from './GeneralSettingsForm.module.scss';
import PremiumSwitch from './PremiumSwitch';

const GeneralSettingsForm = () => {
  const {
    data: { company },
    actions: { updateUser },
  } = useContext(AuthContext);

  const { showSuccess, showError } = useNotification();

  const initialValues = {
    record_transcript: !!company?.record_transcript,
    record_audio: !!company?.record_audio,
  };

  const formSchema = z.object({
    record_transcript: z.boolean().default(true),
    record_audio: z.boolean().default(true),
  });

  const { form, error, loading } = useFormHandler(formSchema, {
    initialValues,
  });

  const handleValueChange = (value: boolean, field: string) => {
    form.setFieldValue(field, value);
    form.onSubmit(onSubmit)();
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const data = { ...company, ...values };

    try {
      await updateUser(data);
      showSuccess('General settings updated successfully');
    } catch (error) {
      logger.error('General settings form error', error as Error, {
        errorType: ErrorType.VALIDATION_ERROR,
        component: 'GeneralSettingsForm'
      });
      showError('Error. Please try again.');
    }
  };

  return (
    <Box px="lg">
      <Grid gutter={20} mb={{ base: 20, lg: 30 }}>
        <Grid.Col span={{ lg: 8, base: 12 }}>
          <Text className={classes.title} mb={2} fw={600}>
            Call Recording and Transcription
          </Text>
          <Text mb={2} size="sm" c="dimmed">
            Note: When call recording or transcription is enabled, all
            participants will be notified. Transferred calls are not transcribed
            or recorded.
          </Text>
        </Grid.Col>
        <Grid.Col span={{ lg: 8, base: 12 }}>
          <Form form={form} error={error} onSubmit={onSubmit}>
            <PremiumSwitch
              name="record_audio"
              label="Enable call recording"
              description="Save audio recording for each call with the AI receptionist."
              loading={loading}
              onValueChange={handleValueChange}
            />
            <PremiumSwitch
              name="record_transcript"
              label="Enable call transcription"
              description="Save the text transcription for each call with the AI receptionist."
              loading={loading}
              onValueChange={handleValueChange}
            />
          </Form>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default GeneralSettingsForm;
